import { Store } from '../../net/store'
import { RELOAD_KEY, canAutoReload, parseReloadAt } from './reloadGuard'

/** sessionStorage, or null when the browser blocks it. */
export type ReloadStorage = Pick<Storage, 'getItem' | 'setItem'> | null

/**
 * `ok`: nothing failed. `deferred`: a chunk failed mid-race; the reload waits for the menu.
 * `failed`: a chunk failed and the page will not reload by itself (it just did, or it cannot
 * remember that it did): the UI offers a Reload button instead.
 */
export type ChunkReloadState = 'ok' | 'deferred' | 'failed'

export interface ChunkReloadDeps {
  storage: ReloadStorage
  now: () => number
  reload: () => void
}

/**
 * What the page does when a lazy chunk will not load (D-066): after a client deploy Cloudflare
 * Pages answers a missing hashed chunk with `index.html`, so the import fails. The page reloads
 * once to pick up the new build, never twice within `RELOAD_WINDOW_MS`, and never mid-race
 * (`setBusy`): then it waits until the player is back on the menu.
 */
export class ChunkReload {
  readonly state = new Store<ChunkReloadState>('ok')
  private busy: () => boolean = () => false
  private reloading = false

  constructor(private readonly deps: ChunkReloadDeps) {}

  /** While this says true (a race or lobby is up) a failure only defers the reload. */
  setBusy(busy: () => boolean): void {
    this.busy = busy
  }

  /** A chunk failed to load (a `vite:preloadError`, or a lazy import's rejection). */
  failed(): void {
    if (this.reloading) return
    if (this.busy()) {
      if (this.state.get() === 'ok') this.state.set('deferred')
      return
    }
    this.tryReload()
  }

  /** The page is idle again (back on the menu): run a deferred reload. */
  idle(): void {
    if (this.state.get() === 'deferred' && !this.busy()) this.tryReload()
  }

  /** The player asked for it (a Reload button): always reloads. */
  reloadNow(): void {
    this.remember(this.deps.now())
    this.reloading = true
    this.deps.reload()
  }

  private tryReload(): void {
    const now = this.deps.now()
    const last = this.lastReloadAt()
    // Without storage the page cannot tell a second failure from the first: never auto-reload.
    if (!canAutoReload(last, now) || !this.remember(now)) {
      this.state.set('failed')
      return
    }
    this.reloading = true
    this.deps.reload()
  }

  private lastReloadAt(): number | null {
    try {
      return parseReloadAt(this.deps.storage?.getItem(RELOAD_KEY))
    } catch {
      return null
    }
  }

  /** Store the reload's time; false when it could not be stored. */
  private remember(now: number): boolean {
    const { storage } = this.deps
    if (!storage) return false
    try {
      storage.setItem(RELOAD_KEY, String(now))
      return parseReloadAt(storage.getItem(RELOAD_KEY)) === now
    } catch {
      return false
    }
  }
}
